/**
 * 決済完了後の遷移先決定ユーティリティ（課題表381）
 *
 * クレジットカード（GMO コールバック /success）と現金（PaymentPending の作業完了検知）は
 * カルテ側で領収書を発行するため領収書発行画面（/inquiries/:id/receipt）へ、
 * 請求書払い・NP後払いは従来どおり決済完了画面（/inquiries/:id/payment-complete）へ遷移する。
 *
 * paymentNavigate.ts と同じく純粋関数 + 網羅性チェックで持ち、
 * 支払方法が増えたときに黙って決済完了画面へ落ちる事故をコンパイル時に検知する。
 */

import type { PaymentMethodCode } from '../data/paymentRepository';

/**
 * Web 直接アクセスで token をクエリで持ち回っている場合に遷移先へ引き継ぐクエリを作る。
 * WebView では token は sessionStorage から復元されるため空になる。
 */
const buildTokenQuery = (tokenParam?: string | null): string =>
  tokenParam ? `?${new URLSearchParams({ token: tokenParam })}` : '';

/** 領収書発行画面のパス */
export const buildReceiptPath = (inquiryId: string, tokenParam?: string | null): string =>
  `/inquiries/${inquiryId}/receipt${buildTokenQuery(tokenParam)}`;

/** 決済完了画面のパス */
export const buildPaymentCompletePath = (inquiryId: string, tokenParam?: string | null): string =>
  `/inquiries/${inquiryId}/payment-complete${buildTokenQuery(tokenParam)}`;

/**
 * /success（GMO / NP後払いのコールバック）からの遷移先を決定する。
 *
 * @param inquiryId 問い合わせ ID
 * @param paymentMethodCode 支払方法コード（未登録・取得失敗は null/undefined）
 * @param tokenParam URL クエリの token（あれば引き継ぐ）
 */
export const resolvePaymentSuccessPath = (
  inquiryId: string,
  paymentMethodCode: PaymentMethodCode | null | undefined,
  tokenParam?: string | null,
): string => {
  // 支払情報が取れない場合は領収書画面を出せないため、アンケート導線を塞がない決済完了画面へ
  if (!paymentMethodCode) {
    return buildPaymentCompletePath(inquiryId, tokenParam);
  }

  switch (paymentMethodCode) {
    case 'credit':
      return buildReceiptPath(inquiryId, tokenParam);
    case 'cash': // 現金は PaymentPending 経由で領収書画面へ行く（/success は通らない）
    case 'postpaid': // 領収書は発行しない（事業者へ問い合わせ）
    case 'bill': // 同上
      return buildPaymentCompletePath(inquiryId, tokenParam);
    default: {
      // 将来 PaymentMethodCode に新たな値が追加された場合、コンパイル時に検知する
      const _exhaustive: never = paymentMethodCode;
      return _exhaustive;
    }
  }
};
